<template>
<!-- eslint-disable max-len -->
  <edit-page
    @initialized="refreshRoleOverrideList"
    ref="editPage"
    name="Domain Branding"
    model-name="config"
    param="signinConfigId"
    page-prefix="/blade/domainbrandings"
    :create="createSignInConfig"
    :fetch="getSignInConfig"
    :update="updateSignInConfig"
    :remove="deleteSignInConfig"
    :stub="config"
    :validator="validator"
    :utils="utils">
    <template slot-scope="{ model: config, validator }">
      <!-- metadata -->
      <text-input label v-model.trim="config.name" :validator="validator.name" required autofocus>Name</text-input>
      <checkbox-input v-model="config.default">Default</checkbox-input>


      <checkbox-input v-model="config.enableTicketNotifications">Enable Ticket Submission Notifications</checkbox-input>
      <template v-if="config.enableTicketNotifications" >
        <text-input @input="emailListFlip = !emailListFlip" label v-model.trim="config.ticketEmailList">Ticket Notification Recipients</text-input>
        <div class="email-list-recipients">Ticket submission notifications will be sent to {{ticketEmailRecipientList}}</div>
      </template>

      <checkbox-input v-model="config.enableRegistrationNotifications">Enable Account Registration Notifications</checkbox-input>

      <template v-if="config.enableRegistrationNotifications" >
        <text-input @input="emailListFlip = !emailListFlip" label v-model.trim="config.registrationEmailList">Registration Notification Recipients</text-input>
        <div class="email-list-recipients">Registration notifications will be sent to {{emailRecipientList}}</div>
      </template>

      <checkbox-input v-model="config.uniqueIdSignin" :disabled="!canManageGames">Use Unique ID Sign In</checkbox-input>
      <checkbox-input v-if="config.uniqueIdSignin" v-model="config.uniqueIdSigninAutoRegister" :disabled="!canManageGames">Automatically Create Unique ID Accounts</checkbox-input>

      <template v-if="config.uniqueIdSignin && config.uniqueIdSigninAutoRegister">
        <!-- override autogen first/last name (overrideAutogenFirstName && *LastName) -->
        <text-input v-model.trim="config.overrideAutogenFirstName" label>Override Autogenerated First Name</text-input>
        <text-input v-model.trim="config.overrideAutogenLastName" label>Override Autogenerated Last Name</text-input>
      </template>

      <select-input
        v-if="config.uniqueIdSignin && config.uniqueIdSigninAutoRegister"
        v-model="config.uniqueIdClass"
        :disabled="!canManageGames"
        label
        :options="classes"
        use="_id"
        :display="e => { return `${e.className}`; }"
        :to="(id) => { return canManageGames ? `/blade/classes/${id}/edit` : null; }"
      >
        Automatic Sign Up Target {{classTypeName}}
      </select-input>
      <text-input v-if="config.uniqueIdSignin && config.uniqueIdSigninAutoRegister" label v-model.trim="config.qualtricsRedirectURL">Centiment Redirect URL</text-input>
      <text-input v-if="config.uniqueIdSignin && config.uniqueIdSigninAutoRegister" label v-model.trim="config.qualtricsExitURL">Centiment Exit URL</text-input>
      <text-input label v-model.trim="config.verificationEmailSenderName">Verification Email Sender Name (leave blank to use the default for this B.L.A.D.E. instance)</text-input>
      <checkbox-input v-model="config.enableHubContentChangeNotifications">Send Emails To Users Whenever The Content On Their Home Page Changes</checkbox-input>
      <text-input label v-model.trim="config.domain" :validator="validator.domain">Domain</text-input>
      <text-input label v-model.trim="config.tabText">Tab Text</text-input>
      <checkbox-input v-model="config.useInternalHelp">Use Internal Support Requests</checkbox-input>
      <checkbox-input v-if="config.useInternalHelp" v-model="config.hideHelpFromNavbar">
        Hide Support Requests From Navbar (access via FAQ)
      </checkbox-input>
      <text-input v-if="!(config.useInternalHelp)" label v-model.trim="config.helpUrl">
        Help (URL)
      </text-input>
      <checkbox-input v-model="config.useInternalFaq">Use Internal FAQ</checkbox-input>
      <text-input v-if="!(config.useInternalFaq)" label v-model.trim="config.faqUrl">
        FAQ (URL)
      </text-input>
      <text-input label v-model.trim="config.corporateSiteUrl">Corporate Site URL</text-input>
      <text-input label v-model.trim="config.corporateSiteText">Corporate Site Text</text-input>
      <text-input label v-model.trim="config.classWorkspaceLabelOverride">Class/Workgroup Label Override</text-input>
      <text-input v-if="config.classWorkspaceLabelOverride" :placeholder="`${config.classWorkspaceLabelOverride}s`" label v-model.trim="config.classWorkspaceLabelOverridePlural">Class/Workgroup Label Override (Plural)</text-input>

      <img v-if="config.navbarLogo" class="form-image-background" :src="resolveAssetPath(config.navbarLogo, 'ui/customer')">

      <asset-select-input use-private-assets label v-model="config.navbarLogo" legacy-manifest-path="ui/customer">Navbar Logo</asset-select-input>

      <img v-if="config.backgroundImage" class="form-image-background" :src="resolveAssetPath(config.backgroundImage, 'ui/customer')">
      <asset-select-input use-private-assets label v-model="config.backgroundImage" legacy-manifest-path="ui/customer">Background Image</asset-select-input>

      <select-input
        :disabled="isProfessor"
        label
        v-model="config.environment"
        :options="environments"
        use="_id"
        :display="e => { return `${e.name}`; }"
        :to="(id) => { return !isProfessor ? `/blade/environments/${id}/edit` : null; }"
      >
        Gamulation Branding (currently only affects navbar colors and button colors)
      </select-input>

      <h2 style="margin-top: 40px;">Midsection</h2>
      <img v-if="config.midsectionImage" :style="`padding: 10px; background-color: ${config.brandPrimaryColor || 'rgb(255,255,255)'}`" class="form-image-background" :src="resolveAssetPath(config.midsectionImage, 'ui/customer')">
      <asset-select-input use-private-assets label v-model="config.midsectionImage" legacy-manifest-path="ui/customer">Midsection Brand Image</asset-select-input>

      <text-input label v-model.trim="config.midsectionText" textarea>Midsection Text</text-input>
      <div
        v-if="config.midsectionTextColor != null && config.midsectionTextColor.length > 0"
        :style="`border: 1px solid black; height: 15px; width: 15px; background-color: ${config.midsectionTextColor};`"
      />

      <text-input label v-model.trim="config.midsectionTextColor">Midsection Right Panel Text Color (supports all valid CSS colors, but rgba() recommended)</text-input>

      <div
        v-if="config.brandPrimaryColor != null && config.brandPrimaryColor.length > 0"
        :style="`border: 1px solid black; height: 15px; width: 15px; background-color: ${config.brandPrimaryColor};`"
      />

      <text-input label v-model.trim="config.brandPrimaryColor">Midsection Right Panel Background Color (supports all valid CSS colors, but rgba() recommended)</text-input>

      <div
        v-if="config.signinTextColor != null && config.signinTextColor.length > 0"
        :style="`border: 1px solid black; height: 15px; width: 15px; background-color: ${config.signinTextColor};`"
      />

      <text-input label v-model.trim="config.signinTextColor">Midsection Left Panel Text Color (supports all valid CSS colors, but rgba() recommended)</text-input>

      <div
        v-if="config.signinBackgroundColor != null && config.signinBackgroundColor.length > 0"
        :style="`border: 1px solid black; height: 15px; width: 15px; background-color: ${config.signinBackgroundColor};`"
      />

      <text-input label v-model.trim="config.signinBackgroundColor">Midsection Left Panel Background Color (supports all valid CSS colors, but rgba() recommended)</text-input>

      <select-input label :options="fontOptions" v-model="config.font">Default Font (may be manually overridden)</select-input>

      <h2 style="margin-top: 40px;">Navbar</h2>

      <!-- default settings -->
      <h3 style="margin-top: 20px;">Default Settings</h3>
      <sign-in-config-editor :settings="config.defaultSettings"/>

      <!-- role based settings -->
      <h3 style="margin-top: 40px;">Role Settings</h3>
      <select-input @input="onFontChange" :disabled="roleOptions.length == 0" label :options="roleOptions" v-model="selectedRole">Role</select-input>
      <button
        :disabled="roleOptions.length == 0 || !selectedRole"
        style="margin-bottom: 15px; display: block;"
        class="btn btn-sm"
        @click.prevent="addRoleOverride"
      >
        Add
      </button>

      <div class="role-override" v-for="(role, idx) in specifiedUserRoles" :key="idx">
        <h4>{{role}}</h4>
        <sign-in-config-editor :settings="config.roleOverrides[role]"/>
        <button
          style="margin-bottom: 15px; display: block;"
          class="btn btn-sm"
          @click.prevent="removeRoleOverride(config, role)"
        >
          Remove
        </button>
      </div>
    </template>
  </edit-page>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { mapActions, mapGetters } from 'vuex';

import EditPage from '../../ui/pages/EditPage';

import AssetSelectInput from '../../ui/inputs/AssetSelectInput';
import SelectInput from '../../ui/inputs/SelectInput';
import TextInput from '../../ui/inputs/TextInput';
import CheckboxInput from '../../ui/inputs/CheckboxInput';
import SignInConfigEditor from './components/SignInConfigEditor';

import fonts, { applyFont } from '../../../utils/fonts';

function computeEmailListFromString(list) {
  if (!list || list.length === 0) return ['notifications@redmerproductions.com'];

  const separated = list.split(',');
  if (separated.length === 0) return ['notifications@redmerproductions.com'];
  return separated;
}

export default {
  components: {
    EditPage,
    AssetSelectInput,
    SelectInput,
    TextInput,
    CheckboxInput,
    SignInConfigEditor
  },
  data() {
    return {
      environments: [],
      classes: [],
      roleUpdateFlip: false,
      emailListFlip: false,
      selectedRole: null,
      defaultRoleOptions: ['student', 'employee'],
      specifiedUserRoles: [],
      utils: {
      },
      config: {
        // metadata
        _id: '',
        name: '',
        default: false,
        enableRegistrationNotifications: false,
        registrationEmailList: '',
        enableTicketNotifications: false,
        ticketEmailList: '',
        domain: '',

        uniqueIdSignin: false,
        uniqueIdSigninAutoRegister: false,
        uniqueIdClass: null,
        qualtricsRedirectURL: '',
        qualtricsExitURL: '',

        // settings
        tabText: 'RedmerU',
        helpUrl: 'https://redmeru.zendesk.com',
        faqUrl: 'https://redmeru.zendesk.com',
        corporateSiteUrl: 'https://redmerproductions.com',
        corporateSiteText: '',
        defaultSettings: {
          displayCorporateSite: true,
          displayHelp: true,
          displayFAQ: true,
          displayRedmerBrand: true
        },
        roleOverrides: {},

        environment: null,
        verificationEmailSenderName: '',
        enableHubContentChangeNotifications: false,
        navbarLogo: '',
        backgroundImage: '',
        midsectionImage: '',
        midsectionText: '',

        brandPrimaryColor: 'rgba(0, 0, 0, 0)',
        midsectionTextColor: 'rgba(0, 0, 0, 0)',
        signinBackgroundColor: 'rgba(255, 255, 255, 1)',
        signinTextColor: 'rgba(33, 33, 33, 1)',
        font: 'Aller'
      },
      validator: {
        name: { required }
      }
    };
  },
  computed: {
    ...mapGetters([
      'activeSignInConfig',
      'isProfessor',
      'classTypeName',
      'canManageGames'
    ]),
    fontOptions() {
      return fonts.map(a => a.name);
    },
    roleOptions() {
      if (this.roleUpdateFlip && !this.roleUpdateFlip) return;
      return this.defaultRoleOptions.filter(a => !this.specifiedUserRoles.includes(a));
    },
    emailRecipientList() {
      if (this.emailListFlip && !this.emailListFlip) return;

      let list = computeEmailListFromString((this.$refs.editPage.model.registrationEmailList || '').trim());
      list = list.map(a => `"${a}"`);
      return list.join(', ');
    },
    ticketEmailRecipientList() {
      if (this.emailListFlip && !this.emailListFlip) return;

      let list = computeEmailListFromString((this.$refs.editPage.model.ticketEmailList || '').trim());
      list = list.map(a => `"${a}"`);
      return list.join(', ');
    }
  },
  methods: {
    ...mapActions([
      'getSignInConfig',
      // 'getEnvironmentEnums',
      'createSignInConfig',
      'updateSignInConfig',
      'deleteSignInConfig',
      'getEnvironments',
      'getClasses',
    ]),
    onFontChange() {
      applyFont(this.$refs.editPage.model.font || this.activeSignInConfig.font);
    },
    refreshRoleOverrideList(cfg) {
      this.specifiedUserRoles.length = 0;

      if (!this.$refs.editPage) return;
      const model = cfg || this.$refs.editPage.model;
      if (model.roleOverrides) {
        console.log(JSON.parse(JSON.stringify(model)));
        // eslint-disable-next-line no-restricted-syntax
        for (const key of Object.keys(model.roleOverrides)) {
          this.specifiedUserRoles.push(key);
        }
      }

      // Reactivity hack?  For some reason specifiedUserRoles doesn't
      // seem to be correctly reactive.
      this.roleUpdateFlip = !this.roleUpdateFlip;
      this.$forceUpdate();
    },
    addRoleOverride() {
      this.$refs.editPage.model.roleOverrides[this.selectedRole] = {
        displayCorporateSite: true,
        displayHelp: true,
        displayFAQ: true,
        displayRedmerBrand: true
      };

      this.selectedRole = null;
      this.refreshRoleOverrideList();
    },
    removeRoleOverride(cfg, role) {
      delete cfg.roleOverrides[role];
      this.refreshRoleOverrideList();
    }
  },
  async mounted() {
    this.classes = await this.getClasses();
    this.environments = await this.getEnvironments();
    await this.$nextTick();
    this.refreshRoleOverrideList();
  },
  beforeDestroy() {
    applyFont(this.activeSignInConfig.font);
  }
};
</script>

<style lang="scss" scoped>
.form-image-background + span {
  display: block;
  margin: -10px 0 10px 10px;
}
.form-image-background {
  background-color: rgba(0, 0, 0, 0);
}
.role-override
{
  border: 2px solid rgba(0,0,0,0.4);
  padding: 10px;
  margin: 5px;
  margin-bottom: 10px;
}
.email-list-recipients
{
  margin-top: 0px;
  margin-bottom: 10px;
  font-style: italic;
  color: rgba(0,0,0,0.5);
}
</style>



// WEBPACK FOOTER //
// src/components/blade/pages/SignInEdit.vue